import React, { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import FinePrint from './FinePrint';
import StampButton from '../public/StampButton';
import { gsap, SplitText, EASE, prefersReducedMotion } from '../public/gsap';

// Magnification of the annotated layer inside the lens.
const ZOOM = 1.18;
const IDLE_LABEL = "JPRO · reading";

const LEDGER = [
    ["Reads", "Regulations, tenant files, rent rolls"],
    ["Flags", "Set-aside, rent and income-limit risk"],
    ["Decides", "You. Every time."],
];

// § 01 — "Read the fine print". A page of § 42 sits beside the headline.
// A loupe moves across it (following the cursor, or press-and-hold on touch,
// or touring JPRO's findings when idle) and reveals the annotated page beneath.
function Hero({ setIsChatOpen }) {
    const rootRef = useRef(null);
    const headlineRef = useRef(null);
    const sheetRef = useRef(null);
    const plainRef = useRef(null);
    const labelRef = useRef(null);

    // Intro: headline sets line by line, the page slides up, rules draw in.
    useLayoutEffect(() => {
        const reduced = prefersReducedMotion();
        const ctx = gsap.context(() => {
            if (reduced) return;
            gsap.set(headlineRef.current, { autoAlpha: 0 });
            gsap.set(".hero-fade", { autoAlpha: 0, y: 16 });
            gsap.set(".hero-rule", { scaleX: 0, transformOrigin: "left center" });
            gsap.set(".hero-sheet", { clipPath: "inset(100% 0% 0% 0%)" });
            gsap.set(".hero-underline", { strokeDashoffset: 1 });

            let split;
            document.fonts.ready.then(() => {
                if (!headlineRef.current) return;
                gsap.set(headlineRef.current, { autoAlpha: 1 });
                split = SplitText.create(headlineRef.current, {
                    type: "lines",
                    mask: "lines",
                    autoSplit: true,
                    onSplit: (self) =>
                        gsap.from(self.lines, { yPercent: 105, duration: 1.3, ease: EASE, stagger: 0.09 }),
                });
                gsap.timeline({ delay: 0.35 })
                    .to(".hero-underline", { strokeDashoffset: 0, duration: 0.9, ease: "power2.inOut" }, 0.5)
                    .to(".hero-fade", { autoAlpha: 1, y: 0, duration: 1, ease: EASE, stagger: 0.08 }, 0.6)
                    .to(".hero-sheet", { clipPath: "inset(0% 0% 0% 0%)", duration: 1.5, ease: "expo.inOut" }, 0.2)
                    .to(".hero-rule", { scaleX: 1, duration: 1.1, ease: EASE, stagger: 0.1 }, 0.9);
            });
            return () => split?.revert();
        }, rootRef);
        return () => ctx.revert();
    }, []);

    // The loupe.
    useLayoutEffect(() => {
        const sheet = sheetRef.current;
        if (!sheet) return;
        const reduced = prefersReducedMotion();
        const radius = () => (window.innerWidth < 640 ? 92 : 124);
        const state = { x: sheet.clientWidth * 0.6, y: sheet.clientHeight * 0.35, r: 0 };

        const apply = () => {
            sheet.style.setProperty("--lx", `${state.x}px`);
            sheet.style.setProperty("--ly", `${state.y}px`);
            sheet.style.setProperty("--lr", `${state.r}px`);
        };
        apply();

        const setLabel = (text) => {
            if (labelRef.current && labelRef.current.textContent !== text) labelRef.current.textContent = text;
        };

        // Findings are read from the plain layer: it isn't scaled, so its
        // rects map straight onto sheet coordinates.
        const findings = () => {
            const s = sheet.getBoundingClientRect();
            return [...plainRef.current.querySelectorAll("[data-clause]")]
                .map((el) => {
                    const r = el.getClientRects()[0] || el.getBoundingClientRect();
                    const edge = radius() * 0.7;
                    const x = gsap.utils.clamp(edge, s.width - edge, r.left - s.left + Math.min(r.width, 160) / 2);
                    return { x, y: r.top - s.top + r.height / 2, clause: el.dataset.clause };
                })
                .filter((p) => p.y > 40 && p.y < s.height - 60);
        };

        const nearest = (x, y) => {
            let best = null;
            let bestD = 150;
            for (const p of findings()) {
                const d = Math.hypot(p.x - x, p.y - y);
                if (d < bestD) { bestD = d; best = p; }
            }
            return best ? `JPRO · ${best.clause}` : IDLE_LABEL;
        };

        const xTo = gsap.quickTo(state, "x", { duration: reduced ? 0 : 0.55, ease: "power3.out", onUpdate: apply });
        const yTo = gsap.quickTo(state, "y", { duration: reduced ? 0 : 0.55, ease: "power3.out", onUpdate: apply });

        let tour = null;
        let resumeTimer = null;
        let inView = true;

        const stopTour = () => { tour?.kill(); tour = null; clearTimeout(resumeTimer); };
        const startTour = () => {
            stopTour();
            const pts = findings();
            if (!pts.length) return;
            if (reduced) {
                const p = pts[Math.min(2, pts.length - 1)];
                Object.assign(state, { x: p.x, y: p.y, r: radius() });
                apply();
                setLabel(`JPRO · ${p.clause}`);
                return;
            }
            tour = gsap.timeline({ repeat: -1, paused: !inView });
            pts.forEach((p) => {
                tour.to(state, { x: p.x, y: p.y, duration: 1.7, ease: "power2.inOut", onUpdate: apply, onStart: () => setLabel(IDLE_LABEL) })
                    .call(() => setLabel(`JPRO · ${p.clause}`))
                    .to({}, { duration: 1.6 });
            });
        };
        const resumeLater = () => { clearTimeout(resumeTimer); resumeTimer = setTimeout(startTour, 1400); };

        const local = (clientX, clientY) => {
            const s = sheet.getBoundingClientRect();
            return [clientX - s.left, clientY - s.top];
        };

        // Mouse / pen: the loupe follows the pointer.
        const onPointerMove = (e) => {
            if (e.pointerType === "touch") return;
            stopTour();
            const [x, y] = local(e.clientX, e.clientY);
            if (state.r < radius()) gsap.to(state, { r: radius(), duration: 0.4, ease: EASE, onUpdate: apply });
            xTo(x); yTo(y);
            setLabel(nearest(x, y));
        };
        const onPointerLeave = (e) => { if (e.pointerType !== "touch") resumeLater(); };

        // Touch: press and hold to pick up the loupe, so normal scrolling still works.
        let holdTimer = null;
        let holding = false;
        let startPt = null;
        const onTouchStart = (e) => {
            const t = e.touches[0];
            startPt = [t.clientX, t.clientY];
            holdTimer = setTimeout(() => {
                holding = true;
                stopTour();
                const [x, y] = local(...startPt);
                xTo(x); yTo(y);
                gsap.to(state, { r: radius() * 1.1, duration: 0.3, ease: EASE, onUpdate: apply });
                setLabel(nearest(x, y));
            }, 220);
        };
        const onTouchMove = (e) => {
            const t = e.touches[0];
            if (!holding) {
                if (startPt && Math.hypot(t.clientX - startPt[0], t.clientY - startPt[1]) > 8) clearTimeout(holdTimer);
                return;
            }
            e.preventDefault();
            const [x, y] = local(t.clientX, t.clientY);
            xTo(x); yTo(y);
            setLabel(nearest(x, y));
        };
        const onTouchEnd = () => {
            clearTimeout(holdTimer);
            if (holding) {
                holding = false;
                gsap.to(state, { r: radius(), duration: 0.3, ease: EASE, onUpdate: apply });
                resumeLater();
            }
        };

        sheet.addEventListener("pointermove", onPointerMove);
        sheet.addEventListener("pointerleave", onPointerLeave);
        sheet.addEventListener("touchstart", onTouchStart, { passive: true });
        sheet.addEventListener("touchmove", onTouchMove, { passive: false });
        sheet.addEventListener("touchend", onTouchEnd);
        sheet.addEventListener("touchcancel", onTouchEnd);

        // Pause the idle tour while the hero is off screen.
        const io = new IntersectionObserver(([entry]) => {
            inView = entry.isIntersecting;
            if (tour) inView ? tour.resume() : tour.pause();
        });
        io.observe(sheet);

        let resizeTimer = null;
        const onResize = () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(() => { if (!holding) startTour(); }, 250); };
        window.addEventListener("resize", onResize);

        // The loupe opens once the page has slid in.
        const intro = reduced
            ? (startTour(), null)
            : gsap.delayedCall(2.1, () => {
                gsap.to(state, { r: radius(), duration: 0.9, ease: EASE, onUpdate: apply });
                startTour();
            });

        return () => {
            intro?.kill();
            stopTour();
            clearTimeout(holdTimer);
            clearTimeout(resizeTimer);
            gsap.killTweensOf(state);
            io.disconnect();
            window.removeEventListener("resize", onResize);
            sheet.removeEventListener("pointermove", onPointerMove);
            sheet.removeEventListener("pointerleave", onPointerLeave);
            sheet.removeEventListener("touchstart", onTouchStart);
            sheet.removeEventListener("touchmove", onTouchMove);
            sheet.removeEventListener("touchend", onTouchEnd);
            sheet.removeEventListener("touchcancel", onTouchEnd);
        };
    }, []);

    return (
        <section ref={rootRef} className="relative">
            <div className="grid gap-12 pt-10 pb-8 lg:grid-cols-12 lg:gap-10 lg:pt-14 lg:pb-28">
                {/* Copy column */}
                <div className="flex flex-col justify-between lg:col-span-5">
                    <div>
                        <p className="hero-fade mb-8 font-plex-mono text-[12px] uppercase tracking-[0.2em] text-stamp">
                            § 01 &nbsp;Read the fine print
                        </p>
                        <h1
                            ref={headlineRef}
                            className="font-news text-[44px] font-normal leading-[1.02] tracking-[-0.02em] text-ink [text-wrap:balance] sm:text-[58px] lg:text-[60px] xl:text-[68px] dark:text-paper"
                        >
                            Compliance lives in the{" "}
                            <span className="relative inline-block whitespace-nowrap italic">
                                fine print.
                                <svg
                                    aria-hidden="true"
                                    viewBox="0 0 300 16"
                                    preserveAspectRatio="none"
                                    className="absolute -bottom-1 left-0 h-[0.18em] w-full overflow-visible text-stamp"
                                >
                                    <path
                                        className="hero-underline"
                                        d="M3 11 C 70 4, 150 3, 297 7"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        pathLength="1"
                                        strokeDasharray="1"
                                    />
                                </svg>
                            </span>{" "}
                            JPRO reads every line.
                        </h1>
                        <p className="hero-fade mt-8 max-w-md font-plex text-[17px] leading-relaxed text-ink/70 dark:text-paper/70">
                            An intelligent compliance tool for affordable housing. JPRO reads the regulations, your files and
                            your numbers, flags what&rsquo;s at risk, and leaves the final call with you.
                        </p>
                        <div className="hero-fade mt-10 flex flex-wrap items-center gap-x-8 gap-y-5">
                            <StampButton onClick={() => setIsChatOpen(true)}>Ask JPRO</StampButton>
                            <Link
                                to="/about"
                                className="link-underline font-plex text-[15px] text-ink/80 hover:text-ink dark:text-paper/80 dark:hover:text-paper"
                            >
                                Explore JPRO
                            </Link>
                        </div>
                    </div>

                    <dl className="mt-14 lg:mt-0">
                        {LEDGER.map(([term, detail]) => (
                            <div key={term} className="relative grid grid-cols-[96px_1fr] py-3 font-plex text-[14px]">
                                <span aria-hidden="true" className="hero-rule absolute inset-x-0 top-0 h-px bg-ink/20 dark:bg-paper/20" />
                                <dt className="font-plex-mono text-[11px] uppercase tracking-[0.16em] leading-[21px] text-ink/50 dark:text-paper/50">{term}</dt>
                                <dd className="text-ink/80 dark:text-paper/80">{detail}</dd>
                            </div>
                        ))}
                    </dl>
                </div>

                {/* The page */}
                <div className="lg:col-span-7">
                    <div className="hero-sheet relative border border-ink/15 bg-paper-50 dark:border-paper/15 dark:bg-ink-900">
                        <div className="flex items-baseline justify-between border-b border-ink/15 px-5 py-3 font-plex-mono text-[10.5px] uppercase tracking-[0.14em] text-ink/55 dark:border-paper/15 dark:text-paper/55">
                            <span>26 U.S.C. § 42 · Low-income housing credit</span>
                            <span className="hidden sm:inline">Excerpt</span>
                        </div>

                        <div
                            ref={sheetRef}
                            className="relative h-[440px] select-none overflow-hidden sm:h-[560px] lg:h-[600px] [@media(hover:hover)]:cursor-none"
                            style={{ "--lx": "60%", "--ly": "35%", "--lr": "0px", touchAction: "pan-y" }}
                        >
                            {/* Plain fine print */}
                            <div ref={plainRef} className="absolute inset-0 px-5 pt-5">
                                <FinePrint />
                            </div>

                            {/* Annotated page, magnified and clipped to the loupe */}
                            <div
                                aria-hidden="true"
                                className="pointer-events-none absolute inset-0 bg-paper-50 px-5 pt-5 dark:bg-ink-900"
                                style={{
                                    clipPath: `circle(calc(var(--lr) / ${ZOOM}) at var(--lx) var(--ly))`,
                                    transform: `scale(${ZOOM})`,
                                    transformOrigin: "var(--lx) var(--ly)",
                                }}
                            >
                                <FinePrint annotated />
                            </div>

                            {/* Loupe ring with reticle ticks and readout */}
                            <div
                                aria-hidden="true"
                                className="pointer-events-none absolute left-0 top-0 rounded-full border border-ink/70 dark:border-paper/70"
                                style={{
                                    width: "calc(var(--lr) * 2)",
                                    height: "calc(var(--lr) * 2)",
                                    transform: "translate(calc(var(--lx) - var(--lr)), calc(var(--ly) - var(--lr)))",
                                }}
                            >
                                <span className="absolute left-1/2 top-0 h-2 w-px -translate-x-1/2 bg-ink/70 dark:bg-paper/70" />
                                <span className="absolute bottom-0 left-1/2 h-2 w-px -translate-x-1/2 bg-ink/70 dark:bg-paper/70" />
                                <span className="absolute left-0 top-1/2 h-px w-2 -translate-y-1/2 bg-ink/70 dark:bg-paper/70" />
                                <span className="absolute right-0 top-1/2 h-px w-2 -translate-y-1/2 bg-ink/70 dark:bg-paper/70" />
                                <span
                                    ref={labelRef}
                                    className="absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap bg-ink px-2 py-0.5 font-plex-mono text-[10.5px] tracking-[0.04em] text-paper dark:bg-paper dark:text-ink"
                                >
                                    {IDLE_LABEL}
                                </span>
                            </div>

                            {/* Page runs off the bottom */}
                            <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-paper-50 to-transparent dark:from-ink-900" />
                        </div>
                    </div>
                    <p className="hero-fade mt-3 font-plex-mono text-[11px] uppercase tracking-[0.14em] text-ink/45 dark:text-paper/45">
                        <span className="hidden [@media(hover:hover)]:inline">Move across the page to read it with JPRO</span>
                        <span className="[@media(hover:hover)]:hidden">Press and hold the page to read it with JPRO</span>
                        <span className="text-ink/30 dark:text-paper/30"> · sample property, illustrative findings</span>
                    </p>
                </div>
            </div>
        </section>
    );
}

export default Hero;
